import React, { useState, useEffect, isValidElement } from 'react';
import {
  Form,
  Row,
  Input,
  DatePicker,
  Select,
  Divider,
  Modal,
  InputNumber,
  Col,
  Switch,
  Alert,
  Radio,
  Card,
  Tabs
} from 'antd';
import {
  DrawerEdit,
  FormItemWrap,
  normalizeFromSelect,
  normalizeToSelect,
  api,
  useCrudEditContext
} from 'react-structure-admin';
import {
  FormListItemWrap,
  FormListWrap,
  InputCurrency,
  RemoteSelectTree,
  RemoteSelectCrud,
  ResCol,
  UploadList,
  SharingFileQRCode
} from 'components';
import moment from 'moment';
import { PAYMENT, RECEIPT } from 'constants/types';
import { formatFederalTaxNumber } from 'util/formatters';
import RemoteSelect from 'components/RemoteSelect';
import { useDispatch } from 'react-redux';
import lodash from 'lodash';
import {
  create,
  createByEvent,
  update
} from 'stores/financialEntries/financialEntryActions';
import useCostCenter from 'core/hooks/useCostCenter';
import { EditOutlined } from '@ant-design/icons';
import useAuth from 'core/hooks/useAuth';
import usePermissions from 'core/hooks/usePermissions';
import SupplierEdit from '../suppliers/SupplierEdit';
import FinancialEntryInstallmentNumber from './FinancialEntryInstallmentNumber';
import FinancialEntryFormPaymentItem from './FinancialEntryFormPaymentItem';
import FinancialEntryToolbarEdit from './FinancialEntryToolbarEdit';
import CustomerEdit from '../customers/CustomerEdit';

const minimumValueRule = {
  required: true,
  message: 'Valor total é obrigatório',
  type: 'number',
  min: 0.01
};

const FinancialEntryEdit = (props) => {
  const { user, accountantId } = useAuth();
  const {
    data,
    onCreateOrUpdateSuccess,
    suggestAsDefaultCheckingAccount = null
  } = props;
  const { settings } = user;
  const [hasOneReceived, setHasOneReceived] = useState(false);
  const [numberOfInstallments, setNumberOfInstallments] = useState(0);
  const [fieldEditing, setFieldEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const { isFinancialAdministrator } = usePermissions();
  const [tabKey, setTabKey] = useState(
    isFinancialAdministrator ? 'tab_customer' : 'tab_patient'
  );
  const { enabledCostCenter } = useCostCenter();
  const { form } = useCrudEditContext();

  const dispatch = useDispatch();
  const isEditing = !!data?.id;

  const handleBeforeSubmit = ({
    effectiveDate,
    type,
    status = 'awaitingPayment',
    patient,
    customer,
    supplier,
    costCenter,
    attachments = [],
    items = [],
    recurring,
    ...rest
  }) => {
    if (!isEditing && !recurringEnabled) {
      recurring = null;
    }

    if (tabKey === 'tab_patient') {
      customer = null;
    }

    if (tabKey === 'tab_customer') {
      patient = null;
    }

    return {
      patient: patient
        ? {
            id: patient.key,
            name: isValidElement(patient.label)
              ? patient.label.props.label
              : patient.label
          }
        : null,
      customer: customer
        ? {
            id: customer.key,
            name: isValidElement(customer.label)
              ? customer.label.props.label
              : customer.label
          }
        : null,
      supplier: supplier
        ? {
            id: supplier.key,
            name: isValidElement(supplier.label)
              ? supplier.label.props.label
              : supplier.label
          }
        : null,
      type,
      status,
      effectiveDate: moment(effectiveDate).format('YYYY-MM-DD'),
      costCenter: normalizeFromSelect(costCenter),
      attachments: attachments.map((c) => ({ attachment: c })),
      recurring,
      items: items.map((c) => ({
        ...c,
        dueDate: moment(c.dueDate).format('YYYY-MM-DD'),
        checkingAccount: normalizeFromSelect(c.checkingAccount),
        payment: {
          ...c.payment,
          date: c.payment.date
            ? moment(c.payment.date).format('YYYY-MM-DD')
            : null,
          dateOfCompensation: c.payment.dateOfCompensation
            ? moment(c.payment.dateOfCompensation).format('YYYY-MM-DD')
            : null,
          creationTime: c.payment.creationTime
            ? moment(c.payment?.creationTime).format('YYYY-MM-DD HH:mm')
            : null
        }
      })),
      ...rest
    };
  };

  const handleSubmit = (values) => {
    if (values?.id) {
      dispatch(update(values, onCreateOrUpdateSuccess));
      return;
    }

    if (data.eventId) {
      dispatch(createByEvent(data.eventId, values, onCreateOrUpdateSuccess));
      return;
    }

    dispatch(create(values, onCreateOrUpdateSuccess));
  };

  const handleCheckLimit = () => {
    form.validateFields().then((values) => {
      const newValues = handleBeforeSubmit({ ...data, ...values });

      if (!enabledCostCenter) {
        handleSubmit(newValues);
        return;
      }

      api
        .post('financial-entries/exceeded-monthly-limit', newValues)
        .then(({ data }) => {
          if (data?.result) {
            Modal.confirm({
              width: '700px',
              okText: 'Continuar',
              cancelText: 'Voltar',
              onOk: () => {
                handleSubmit(newValues);
              },
              title:
                'Limite mensal excedido para esse centro de custo.  Deseja prosseguir?'
            });
            return;
          }

          handleSubmit(newValues);
        });
    });
  };

  const getTitle = () => {
    switch (data?.type) {
      case RECEIPT:
        return data?.id ? 'Editar receita' : 'Nova receita';
      case PAYMENT:
        return data?.id ? 'Editar despesa' : 'Nova despesa';
      default:
        return '';
    }
  };

  const readOnly = data?.invoice?.status === 'authorized';
  const type = data?.type;

  const handleSortItems = (a, b) => {
    return a.installmentNumber < b.installmentNumber ? -1 : 0;
  };

  const hasChangedValues = (values) => {
    return ['totalInstallments', 'amount', 'effectiveDate', 'items'].some(
      (path) => lodash.get(values, path) !== undefined
    );
  };

  const distributeValueToInstallments = (value, number) => {
    const totalValue = parseFloat(value);
    const numInstallments = parseInt(number, 10);

    if (totalValue <= 0 || numInstallments <= 0) {
      return [];
    }

    const valuePerInstallment = totalValue / numInstallments;
    let remainingValue = totalValue;

    const installments = [];

    for (let i = 0; i < numInstallments - 1; i += 1) {
      const installment = parseFloat(valuePerInstallment.toFixed(2));
      installments.push(installment);
      remainingValue -= installment;
    }

    installments.push(parseFloat(remainingValue.toFixed(2)));

    return installments;
  };

  const handleLoadSettings = () => {
    const { items = [], totalInstallments = 1 } = form.getFieldsValue();

    if (totalInstallments > 1) {
      setRecurringEnabled(false);
    }

    setHasOneReceived(items.some((c) => c.isPaid || false));
    setNumberOfInstallments(totalInstallments);
  };

  const handleValuesChange = (changedValues, values) => {
    const { amount, items = [], effectiveDate, totalInstallments = 1 } = values;

    handleLoadSettings();

    if (!hasChangedValues(changedValues)) {
      return;
    }

    const allowsUpdateItem =
      (lodash.get(changedValues, 'amount') !== undefined ||
        lodash.get(changedValues, 'effectiveDate') !== undefined ||
        lodash.get(changedValues, 'totalInstallments') !== undefined) &&
      items.every((c) => !c.isPaid);

    let newItems = items.sort(handleSortItems);

    if (items.length > totalInstallments) {
      newItems = newItems.slice(0, totalInstallments);
    }

    const installments = distributeValueToInstallments(
      amount,
      totalInstallments
    );

    for (let number = 1; number <= totalInstallments; number += 1) {
      const installment = newItems.find((c) => c.installmentNumber === number);
      const installmentAmount = installments[number - 1];
      const nextDate = moment(effectiveDate)
        .clone()
        .add(number - 1, 'month');

      if (!installment) {
        newItems.push({
          installmentNumber: number,
          dueDate: nextDate,
          amount: installmentAmount,
          checkingAccount: normalizeToSelect(
            suggestAsDefaultCheckingAccount ?? settings.checkingAccount
          ),
          isPaid: false,
          payment: {
            interestAmount: 0,
            discount: 0,
            fineAmount: 0,
            netAmount: installmentAmount
          }
        });
      } else {
        const { payment } = installment;

        if (allowsUpdateItem) {
          installment.amount = installmentAmount;
          installment.dueDate = nextDate;
          installment.interestAmount = 0;
          installment.discount = 0;
          installment.fineAmount = 0;
        }

        installment.payment.netAmount =
          installment.amount +
          payment.interestAmount +
          payment.fineAmount -
          payment.discount;
      }
    }

    form.setFieldsValue({ items: newItems });
  };

  const handleBeforeBinding = ({
    type,
    status,
    amount = 0,
    totalInstallments = 1,
    effectiveDate,
    patient,
    supplier,
    customer,
    costCenter,
    attachments = [],
    items = [],
    category,
    ...rest
  }) => {
    if (patient != null) {
      setTabKey('tab_patient');
    }

    if (customer != null) {
      setTabKey('tab_customer');
    }

    const values = {
      ...rest,
      type,
      status,
      patient: patient ? normalizeToSelect(patient) : null,
      customer: customer ? normalizeToSelect(customer) : null,
      supplier: supplier ? normalizeToSelect(supplier) : null,
      amount,
      totalInstallments,
      effectiveDate: effectiveDate ? moment(effectiveDate) : moment(),
      costCenter: normalizeToSelect(costCenter),
      attachments: attachments.map((c) => c.attachment),
      category,
      items: items.map((c) => ({
        ...c,
        dueDate: moment(c.dueDate),
        checkingAccount: normalizeToSelect(c.checkingAccount),
        payment: {
          ...c.payment,
          date: c.payment?.date ? moment(c.payment?.date) : null,
          creationTime: c.payment?.creationTime
            ? moment(c.payment?.creationTime)
            : null,
          dateOfCompensation: c.payment?.dateOfCompensation
            ? moment(c.payment?.dateOfCompensation)
            : null
        }
      }))
    };

    if (!rest.id) {
      handleValuesChange({ totalInstallments }, values);
    }

    if (category) {
      setCurrentCategory(category);
    }

    setHasOneReceived(items.some((c) => c.isPaid || false));

    return values;
  };

  const changeField = (fieldKey, newValues) => {
    const value = lodash.merge(
      form.getFieldValue(['items', fieldKey]),
      newValues
    );

    form.setFields([{ name: ['items', fieldKey], value }]);
  };

  const openEditingField = (editing, field) => {
    const { items = [] } = form.getFieldsValue();

    if (editing && items.some((c, index) => !c.isPaid && index < field.name)) {
      Modal.confirm({
        width: '700px',
        okText: 'Continuar',
        cancelText: 'Voltar',
        onCancel: () => {
          changeField(field.name, { isPaid: false });
        },
        onOk: () => {
          setFieldEditing(field);
        },
        title: 'Existe parcela anterior em aberta. Deseja prosseguir?'
      });
      return;
    }

    setFieldEditing(editing ? field : null);
  };

  const closeEditingField = () => {
    handleLoadSettings();
    openEditingField(false);
  };

  const allowsShowCategoryAlert = () => {
    if (!currentCategory || !accountantId) {
      return false;
    }

    return currentCategory.hasAccountingIssues;
  };

  const handleTabChange = (tab) => {
    const { patient, customer } = form.getFieldsValue();

    if (tab === 'tab_customer' && patient) {
      Modal.confirm({
        title: 'Deseja remover o paciente?',
        onOk: () => {
          form.setFieldsValue({ patient: null });
          setTabKey(tab);
        }
      });

      return;
    }

    if (tab === 'tab_patient' && customer) {
      Modal.confirm({
        title: 'Deseja remover o cliente?',
        onOk: () => {
          form.setFieldsValue({ customer: null });
          setTabKey(tab);
        }
      });

      return;
    }

    setTabKey(tab);
  };

  return (
    <DrawerEdit
      {...props}
      size="sm"
      title={getTitle()}
      onBeforeBinding={handleBeforeBinding}
      submit={handleCheckLimit}
      readOnly={readOnly}
      onValuesChange={handleValuesChange}
      toolbarComponent={(toolbarProps) => (
        <FinancialEntryToolbarEdit {...toolbarProps} record={data} />
      )}
    >
      <Form layout="vertical">
        {fieldEditing && (
          <FinancialEntryFormPaymentItem
            form={form}
            type={type}
            field={fieldEditing}
            onClose={closeEditingField}
          />
        )}
        <Row className="no-padding-last">
          <ResCol span={24}>
            <FormItemWrap
              name="description"
              label="Descrição"
              whitespace
              autoFocus
            >
              <Input.TextArea maxLength={250} disabled={readOnly} />
            </FormItemWrap>
          </ResCol>
        </Row>

        <Row className="no-padding-last">
          <ResCol span={24}>
            {data.type === 'receipt' ? (
              <>
                <Tabs activeKey={tabKey} onChange={handleTabChange}>
                  {!isFinancialAdministrator && (
                    <Tabs.TabPane
                      key="tab_patient"
                      tab="Paciente"
                      tabKey="tab_patient"
                    >
                      <FormItemWrap name="patient" label="Paciente">
                        <RemoteSelect
                          resource="patients"
                          disabled={readOnly || data.eventId}
                          optionRender={(c) => (
                            <Select.Option
                              key={c.key}
                              value={c.key}
                              label={c.label}
                            >
                              <div label={c.label}>
                                <div>{c.item.name}</div>
                                {c.item.federalTaxNumber ? (
                                  <div className="gx-fs-sm">
                                    {formatFederalTaxNumber(
                                      c.item.federalTaxNumber
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            </Select.Option>
                          )}
                        />
                      </FormItemWrap>
                    </Tabs.TabPane>
                  )}
                  <Tabs.TabPane
                    key="tab_customer"
                    tab="Cliente"
                    tabKey="tab_customer"
                  >
                    <FormItemWrap name="customer" label="Cliente">
                      <RemoteSelectCrud
                        resource="customers"
                        textPropName="name"
                        resourceTitle="Cliente"
                        editComponent={CustomerEdit}
                        disabled={readOnly || data.eventId}
                        optionRender={(c) => (
                          <Select.Option
                            key={c.key}
                            value={c.key}
                            label={c.label}
                          >
                            <div label={c.label}>
                              <div>{c.item.name}</div>
                              {c.item.federalTaxNumber ? (
                                <div className="gx-fs-sm">
                                  {formatFederalTaxNumber(
                                    c.item.federalTaxNumber
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </Select.Option>
                        )}
                      />
                    </FormItemWrap>
                  </Tabs.TabPane>
                </Tabs>
              </>
            ) : (
              <FormItemWrap name="supplier" label="Fornecedor">
                <RemoteSelectCrud
                  resource="suppliers"
                  textPropName="name"
                  resourceTitle="Fornecedor"
                  editComponent={SupplierEdit}
                  disabled={readOnly}
                  optionRender={(c) => (
                    <Select.Option key={c.key} value={c.key} label={c.label}>
                      <div label={c.label}>
                        <div>{c.item.name}</div>
                        {c.item.federalTaxNumber ? (
                          <div className="gx-fs-sm">
                            {formatFederalTaxNumber(c.item.federalTaxNumber)}
                          </div>
                        ) : null}
                      </div>
                    </Select.Option>
                  )}
                />
              </FormItemWrap>
            )}
          </ResCol>
        </Row>

        <Row className="no-padding-last">
          <ResCol span={9}>
            <FormItemWrap name="effectiveDate" label="Competência" required>
              <DatePicker
                format="DD/MM/YYYY"
                disabled={readOnly}
                disabledDate={(date) => date.isAfter(moment(), 'day')}
              />
            </FormItemWrap>
          </ResCol>
          <ResCol span={7}>
            <FormItemWrap
              className="ant-form-item-money"
              name="amount"
              label="Valor total"
              rules={[minimumValueRule]}
            >
              <InputCurrency
                disabled={readOnly || data.eventId || hasOneReceived}
              />
            </FormItemWrap>
          </ResCol>

          <ResCol span={8}>
            <FormItemWrap name="totalInstallments" label="N° de parcelas">
              <InputNumber
                disabled={readOnly || hasOneReceived}
                min={1}
                max={60}
              />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Divider orientation="left">Parcelas</Divider>
        <FormListWrap name="items" hideAddButton>
          {({ field, value }) => (
            <>
              <Row className="no-padding-last">
                <Col span={4}>
                  <FormListItemWrap name="installmentNumber" label="Parcela">
                    <FinancialEntryInstallmentNumber form={form} />
                  </FormListItemWrap>
                </Col>
                <Col span={8}>
                  <FormListItemWrap name="dueDate" label="Vencimento" required>
                    <DatePicker
                      format="DD/MM/YYYY"
                      disabled={
                        readOnly || value.isPaid || value.amount === undefined
                      }
                    />
                  </FormListItemWrap>
                </Col>
                <Col span={7}>
                  <FormListItemWrap
                    className="ant-form-item-money"
                    name="amount"
                    label="Valor"
                    rules={[minimumValueRule]}
                  >
                    <InputCurrency
                      disabled={
                        readOnly || value.isPaid || value.amount === undefined
                      }
                    />
                  </FormListItemWrap>
                </Col>
                <Col span={4}>
                  <FormListItemWrap
                    name="isPaid"
                    label={type === RECEIPT ? 'Recebido' : 'Pago'}
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) => openEditingField(checked, field)}
                      disabled={value.amount === undefined}
                    />
                  </FormListItemWrap>
                </Col>
                {value.isPaid && (
                  <Col span={1}>
                    <FormListItemWrap name="isPaid" label=" ">
                      <EditOutlined
                        className="gx-pointer gx-text-info"
                        onClick={() => openEditingField(true, field)}
                      />
                    </FormListItemWrap>
                  </Col>
                )}
              </Row>
            </>
          )}
        </FormListWrap>
        <Row className="no-padding-last">
          <ResCol span={24}>
            <FormItemWrap name="category" label="Categoria" required>
              <RemoteSelectTree
                resource="categories"
                placeholder="Selecione"
                disabled={readOnly}
                onDisabled={({ parentCategoryId }) => {
                  return parentCategoryId === null;
                }}
                params={{ types: [type] }}
                getSubItems={({ subCategories }) => subCategories}
                getTitle={({ name }) => name}
                onSelect={(_, { item }) => setCurrentCategory(item)}
              />
            </FormItemWrap>
          </ResCol>
        </Row>

        {allowsShowCategoryAlert() && (
          <Alert
            className="gx-mb-1"
            showIcon
            description="Regra contábil não configurada para a categoria informada. Favor contactar a contabilidade."
            type="warning"
            closable
          />
        )}

        {enabledCostCenter && (
          <Row className="no-padding-last">
            <ResCol span={24}>
              <FormItemWrap name="costCenter" label="Centro de custo" required>
                <RemoteSelect
                  resource="cost-centers"
                  disabled={readOnly}
                  defaultActiveFirstOption={!data?.id}
                />
              </FormItemWrap>
            </ResCol>
          </Row>
        )}

        {!isEditing && numberOfInstallments === 1 && (
          <Row>
            <ResCol span={8}>
              <FormItemWrap
                name={['recurring', 'enabled']}
                label="Repetir lançamento?"
                valuePropName="checked"
              >
                <Switch onChange={setRecurringEnabled} />
              </FormItemWrap>
            </ResCol>

            <ResCol span={8}>
              <FormItemWrap
                name={['recurring', 'quantity']}
                label="Quantas vezes?"
                hidden={!recurringEnabled}
                required={recurringEnabled}
              >
                <InputNumber min={1} max={60} disabled={!recurringEnabled} />
              </FormItemWrap>
            </ResCol>

            <ResCol span={8}>
              <FormItemWrap
                name={['recurring', 'frequency']}
                label="Frequência"
                hidden={!recurringEnabled}
                required={recurringEnabled}
              >
                <Select disabled={!recurringEnabled}>
                  <Select.Option value="monthly" title="Mensal">
                    Mensal
                  </Select.Option>
                  <Select.Option value="fortnightly" title="Quinzenal">
                    Quinzenal
                  </Select.Option>
                  <Select.Option value="daily" title="Diária">
                    Diária
                  </Select.Option>
                </Select>
              </FormItemWrap>
            </ResCol>
          </Row>
        )}

        <Divider orientation="left">Anexos</Divider>
        <FormItemWrap name="attachments" shouldUpdate={() => false}>
          <UploadList showRemove />
        </FormItemWrap>
      </Form>
    </DrawerEdit>
  );
};

export default FinancialEntryEdit;
